/**
 *  When manually subscribing to an observable in a view component, developers are required
 *  to unsubscribe during ngOnDestroy. This utility method auto-configures and manages that relationship
 *  by monkey-patching the Component::ngOnDestroy() method and internally using the takeUntil RxJS operator.
 *
 *  @code
 *
 *  import {untilViewDestroyed} from 'utils/untilViewDestroyed.ts'
 *
 *  @Component({})
 *  export class TicketDetails {
 *
 *    ngOnDestroy() {
 *       ...
 *    }
 *
 *    loadTickets() {
 *      this.ticketService.loadAll()
 *          .pipe(
 *            untilViewDestroyed(this),
 *            map( ticket => ticket.name )
 *          )
 *          .subscribe( tickets => this.tickets = tickets );
 *    }
 *
 *  }
 *
 *  Utility method to hide complexity of bridging a view component instance to a manual observable subs
 */
import { Observable, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface OnDestroyLike {
  ngOnDestroy(): void;

  [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

/**
 *  Monkey-patch ngOnDestroy and then use `takeUntil()` to complete the source subscription.
 */
export function untilViewDestroyed<T>(
  component: OnDestroyLike
): (source: Observable<T>) => Observable<T> {
  return (source: Observable<T>) => source.pipe(takeUntil(componentDestroyed(component)));
}

/**
 * Unique hashkey
 */
const destroy$ = 'destroy$';

/**
 * Monkey-patch ngOnDestroy() method; if needed.
 *
 * NOTE: If the developer is using `untilViewDestroyed()` in multiple places
 * in the component methods, then ngOnDestroy() may already be patched.
 */
function componentDestroyed(cmpInstance: OnDestroyLike): Observable<boolean> {
  if (!isPatched(cmpInstance)) {
    const stop$ = new ReplaySubject<boolean>();
    const origOnDestroy = cmpInstance.ngOnDestroy || (() => {});

    cmpInstance.ngOnDestroy = () => {
      // Monkey-patch
      stop$.next(true);
      stop$.complete();

      origOnDestroy.call(cmpInstance);
      cmpInstance[destroy$] = undefined;
    };

    cmpInstance[destroy$] = stop$.asObservable();
  }

  return cmpInstance[destroy$];
}

/**
 * Has this view component instance already been monkey-patched ?
 */
function isPatched(cmpInstance: OnDestroyLike): boolean {
  return !!cmpInstance[destroy$];
}
